import { useId, useMemo, useState, type KeyboardEvent, type RefObject } from 'react';
import { useMe, useTeam } from '@/lib/hooks';
import { Avatar } from '@/ui/Avatar';
import { firstName, mentionQuery } from '@shared/record';
import { isSubmitKey } from './util';

// A text box that offers teammates to mention when you type "@", as the mock's comment composer does.

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  /** Offers teammates after "@". Off for notes and calls, which notify nobody. */
  mentions: boolean;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  rows?: number;
}

const strip = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function MentionInput({ value, onChange, onSubmit, placeholder, inputRef, mentions, disabled, invalid, describedBy, rows }: Props) {
  const me = useMe();
  const { active } = useTeam();
  const listId = useId();
  const [caret, setCaret] = useState(0);
  const [index, setIndex] = useState(0);
  const [dismissed, setDismissed] = useState<number | null>(null);

  const query = mentions ? mentionQuery(value.slice(0, caret)) : null;
  const options = useMemo(() => {
    if (!query) return [];
    const q = strip(query.query);
    const matches = active.filter((m) => strip(m.name).split(/\s+/).some((part) => part.startsWith(q)) || strip(m.name).startsWith(q));
    // You rarely mention yourself, so you only show up when your name is typed.
    return matches.filter((m) => m.id !== me.user.id || q.length > 0).slice(0, 6);
  }, [query, active, me.user.id]);
  const open = !!query && options.length > 0 && dismissed !== query.start;
  const current = Math.min(index, Math.max(0, options.length - 1));

  const track = (el: HTMLTextAreaElement) => {
    setCaret(el.selectionStart ?? el.value.length);
  };

  const pick = (memberId: string) => {
    const el = inputRef.current;
    const member = active.find((m) => m.id === memberId);
    if (!el || !query || !member) return;
    const short = firstName(member.name);
    const clash = active.some((m) => m.id !== member.id && firstName(m.name).toLowerCase() === short.toLowerCase());
    const label = clash ? member.name : short;
    const before = value.slice(0, query.start);
    const after = value.slice(caret);
    const insert = `@${label} `;
    const next = before + insert + after.replace(/^\s+/, '');
    onChange(next);
    setIndex(0);
    const pos = before.length + insert.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setIndex((current + (e.key === 'ArrowDown' ? 1 : options.length - 1)) % options.length);
        return;
      }
      if ((e.key === 'Enter' && !e.metaKey && !e.ctrlKey) || e.key === 'Tab') {
        e.preventDefault();
        pick(options[current]!.id);
        return;
      }
      if (e.key === 'Escape') {
        // Closes the list without closing the record.
        e.preventDefault();
        setDismissed(query!.start);
        return;
      }
    }
    if (isSubmitKey(e)) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="ta-wrap">
      <textarea
        ref={inputRef}
        value={value}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={placeholder}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        aria-autocomplete={mentions ? 'list' : undefined}
        aria-expanded={mentions ? open : undefined}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open ? `${listId}-${current}` : undefined}
        onChange={(e) => {
          onChange(e.target.value);
          track(e.target);
          setIndex(0);
        }}
        onSelect={(e) => track(e.currentTarget)}
        onKeyDown={onKeyDown}
        onBlur={() => setDismissed(query?.start ?? null)}
        onFocus={() => setDismissed(null)}
      />
      {open ? (
        <div className="mention-pop" role="listbox" id={listId} aria-label="Mention to notify a teammate">
          <div className="mhead" role="presentation">
            Mention to notify a teammate
          </div>
          {options.map((m, i) => (
            <button
              key={m.id}
              id={`${listId}-${i}`}
              type="button"
              tabIndex={-1}
              role="option"
              aria-selected={i === current}
              className={i === current ? 'mi kb' : 'mi'}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setIndex(i)}
              onClick={() => pick(m.id)}
            >
              <Avatar name={m.name} hue={m.hue} size="xs" decorative />
              <span className="grow">{m.name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
